import type { APIResult } from '@/backend/types'

type FailingApiResult = Extract<APIResult<unknown>, { ok: false }>

/**
 * Custom error class for errors returned by the API. Lets us catch API errors and do useful
 * things with the status/message returned by the API.
 */
export class ApiError extends Error {
  public readonly code: string
  public readonly status?: number
  public readonly originalError: FailingApiResult['error']

  constructor(apiError: FailingApiResult['error']) {
    super(apiError.message)
    this.name = 'ApiError'
    this.code = apiError.code
    this.status = apiError.status
    this.originalError = apiError

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }
  }

  /**
   * Creates an ApiError from a failed API result
   */
  static fromFailedResult(result: FailingApiResult): ApiError {
    return new ApiError(result.error)
  }

  /**
   * Checks if an error is an ApiError instance
   */
  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError
  }

  /**
   * Returns a string representation of the error
   */
  toString(): string {
    return `ApiError: ${this.code} - ${this.message}${this.status ? ` (${this.status})` : ''}`
  }
}
